import { useImmer } from "use-immer";
import moment from "moment";
import { useRef, useState, useEffect, useContext, useMemo } from "react";
import SimpleReactValidator from "simple-react-validator";
import { useDebounce } from "use-debounce";
import { useDelete, usePermission, useStatusSelect } from "../../../shared";
import { AppContext } from "../../../store";
import dayjs from "dayjs";
import { useScheduledSyncListQueries } from "./useScheduledSyncListQueries";
import { useCategoryQueries } from "../../MasterData/hooks/useCategoryQueries";

export const useScheduledSyncList = () => {
  const [_, forceUpdate] = useState(0);
  const formValidator = useRef(
    new SimpleReactValidator({
      autoForceUpdate: { forceUpdate },
      element: (message) => message,
      validators: {
        notPastDate: {
          message: "Date must be in the future.",
          rule: (val) => {
            if (!val) return true;

            const selected = dayjs(val);
            const today = dayjs().startOf("day");

            if (!selected.isValid()) return false;

            return selected.isAfter(today, "day");
          },
          required: false,
        },
      },
    }),
  );

  const [state, setState] = useImmer({
    isLoading: true,
    dataList: {},
    filters: {},
    editId: null,
  });

  const { deleteInfo, onDelete, resetDeleteInfo } = useDelete();
  const moduleName = "scheduledSync";
  const {
    tableData,
    setSorting,
    setPagination,
    setTableState,
    setSearchState,
    clearAllFilters,
  } = useContext(AppContext);
  const { sorting, pagination, search } = tableData[moduleName];

  const [debouncedText] = useDebounce(search, 1000);

  const { listQuery: categoryListQuery } = useCategoryQueries({
    categorylist: true,
  });

  const CategoryOptions = categoryListQuery?.data?.map((v) => {
    return { id: v.id, name: v.name };
  });

  const {
    listQuery,
    updateSheduledDate: { data, mutate: updateAsync, isPending: isUpdating },
    removeScheduledSync,
  } = useScheduledSyncListQueries({
    selectedCategory: tableData?.scheduledSync?.data?.categoryfilter,
    selectedSubCategory: tableData?.scheduledSync?.data?.subcategoryfilter,
    pagination,
    sorting,
    selectedDate: tableData?.scheduledSync?.data?.selectedDate,
    selectedStatus: tableData?.scheduledSync?.data?.selectedStatus,
    debouncedText,
    resetDeleteInfo,
  });

  useEffect(() => {
    // getUnits();
    if (listQuery?.data) {
      setState((draft) => {
        draft.dataList = listQuery?.data || [];
        draft.isLoading = false;
      });
    }
  }, [listQuery?.data]);

  const onSearchHandler = (search) => {
    setSearchState({
      tableName: moduleName,
      key: "search",
      value: search,
    });
    setPagination({
      tableName: moduleName,
      callback: (prevPagination) => ({
        ...prevPagination,
        pageIndex: 1,
      }),
    });
  };

  const categoryValuesHandler = (event, option) => {
    const item = option?.map((item) => {
      return { ...item };
    });
    setTableState({
      tableName: moduleName,
      key: "categoryfilter",
      value: item,
    });
  };

  const onDateChangeHandler = (date) => {
    console.log(date);

    setTableState({
      tableName: moduleName,
      key: "selectedDate",
      value: date,
    });
    setPagination({
      tableName: moduleName,
      callback: (prevPagination) => ({
        ...prevPagination,
        pageIndex: 1,
      }),
    });
  };

  const onChangeStatusHandler = (value) => {
    setTableState({
      tableName: moduleName,
      key: "selectedStatus",
      value: value,
    });
    setPagination({
      tableName: moduleName,
      callback: (prevPagination) => ({
        ...prevPagination,
        pageIndex: 1,
      }),
    });
  };

  const selectStatus = useStatusSelect({ onChangeStatusHandler });

  const onClearFilter = () => {
    clearAllFilters({ tableName: moduleName });
  };

  // Use moment to format date as 'DD-MM-YYYY'
  const setEditBtnhandler = (data) => {
    setState((draft) => {
      draft.editId = data?.id || null;
      draft.selectedDataDate = data?.scheduled_date
        ? moment(data.scheduled_date, "DD-MM-YYYY").format()
        : null;
    });
  };

  const onSaveChanges = async (event) => {
    event.preventDefault();
    if (formValidator.current.allValid()) {
      updateAsync({
        id: state.editId,
        payload: {
          scheduled_date: moment(state.selectedDataDate).format("YYYY-MM-DD"),
        },
      });
    } else {
      formValidator.current.showMessages();
      forceUpdate(_ + 1);
    }
  };

  const onDateChange = (event) => {
    setState((draft) => {
      draft.selectedDataDate = event.target.value;
    });
  };

  useEffect(() => {
    if (data?.success) setEditBtnhandler(null);
  }, [data]);

  const selectedCategoryIds = useMemo(() => {
    return (
      tableData?.scheduledSync?.data?.categoryfilter?.map((c) => c.id) || []
    );
  }, [tableData?.scheduledSync?.data?.categoryfilter]);

  const SubCategoryOptions = useMemo(() => {
    if (!selectedCategoryIds.length || !categoryListQuery?.data) return [];

    return categoryListQuery.data
      .filter((cat) => selectedCategoryIds.includes(cat.id))
      .flatMap((cat) => cat.subData || [])
      .map((sub) => ({
        id: sub.id,
        name: sub.name,
      }));
  }, [selectedCategoryIds, categoryListQuery?.data]);

  const subCategoryValuesHandler = (event, option) => {
    const item = option?.map((item) => {
      return { ...item };
    });
    setTableState({
      tableName: moduleName,
      key: "subcategoryfilter",
      value: item,
    });
  };

  useEffect(() => {
    setTableState({
      tableName: moduleName,
      key: "subcategoryfilter",
      value: [],
    });
  }, [selectedCategoryIds]);

  const confirmDelete = () => {
    removeScheduledSync.mutate({ id: deleteInfo.id });
  };

  const categoryFilter = tableData?.scheduledSync?.data?.categoryfilter;
  const selectedStatus = tableData?.scheduledSync?.data?.selectedStatus;
  const selectedDate = tableData?.scheduledSync?.data?.selectedDate;

  const disableclearButton = !(
    (Array.isArray(categoryFilter) && categoryFilter.length > 0) ||
    (selectedStatus !== null && selectedStatus !== "") ||
    selectedDate !== null ||
    selectedDate !== ""
  );

  return {
    state,
    tableData,
    listQuery,
    CategoryOptions,
    categoryValuesHandler,
    onSearchHandler,
    onDateChangeHandler,
    selectStatus,
    onChangeStatusHandler,
    disableclearButton,
    onClearFilter,
    moduleName,
    setEditBtnhandler,
    onSaveChanges,
    onDateChange,
    isUpdating,
    formValidator,
    SubCategoryOptions,
    subCategoryValuesHandler,
    setPagination,
    setSorting,
    onDelete,
    deleteInfo,
    resetDeleteInfo,
    confirmDelete,
    pagination,
    sorting,
  };
};
